import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import {
  MessageCircle,
  Timer,
  Star,
  ShieldCheck,
  TrendingDown,
  Sparkles,
  ArrowRight,
  KeyRound,
  MapPin,
} from "lucide-react";
import { buildWhatsAppLink, COMPANY } from "@/lib/constants";

/* ===== Animated Counter ===== */
const Counter = ({ to, prefix = "", suffix = "", duration = 1.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  return (
    <span ref={ref}>
      {prefix}{val.toLocaleString("de-DE")}{suffix}
    </span>
  );
};

/* ===== Word reveal ===== */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.span
    initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`inline-block ${className}`}
  >
    {children}
  </motion.span>
);

// Deutsche Autos – BMW + Mercedes-Benz + Audi
const HERO_IMAGES = {
  main: "https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&w=1600&q=80",   // BMW M5 dark
  side: "https://images.unsplash.com/photo-1605152276897-4f618f831968?auto=format&fit=crop&w=1200&q=80", // Detailing / Auto-Aufbereitung close-up
};

const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yParallaxSlow = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const opacityFade = useTransform(scrollYProgress, [0, 0.8], [1, 0.6]);

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative pt-28 pb-20 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-gradient-to-b from-white via-[#F4FBFC] to-white"
    >
      {/* ===== Background FX ===== */}
      <div className="absolute inset-0 bg-dot-grid opacity-50 pointer-events-none" />
      <motion.div
        style={{ y: yParallaxSlow }}
        className="absolute -top-60 -right-40 w-[500px] sm:w-[700px] h-[500px] sm:h-[700px] bg-[#1FA3B5]/15 rounded-full blur-3xl pointer-events-none"
      />
      <motion.div
        style={{ y: yParallax }}
        className="absolute -bottom-60 -left-40 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-[#0F4651]/10 rounded-full blur-3xl pointer-events-none"
      />
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 sm:gap-14 lg:gap-16 items-center">
        {/* ===== LEFT: Copy ===== */}
        <motion.div style={{ opacity: opacityFade }} className="lg:col-span-6 relative z-10 order-1">
          {/* Live badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-full bg-white border border-[#1FA3B5]/20 shadow-sm shadow-[#1FA3B5]/10 mb-6 sm:mb-7"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1FA3B5] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1FA3B5]" />
            </span>
            <span className="text-xs sm:text-sm font-semibold text-slate-800">
              <span className="hidden sm:inline">Spezialist für Leasingrückläufer · </span>
              {COMPANY.tagline}
            </span>
          </motion.div>

          <h1 className="font-heading text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-[5.5rem] font-bold tracking-tight text-slate-900">
            <Reveal delay={0.05}>Leasing-</Reveal>{" "}
            <Reveal delay={0.15}>rückgabe?</Reveal>
            <br />
            <span className="relative inline-block">
              <Reveal delay={0.3} className="relative z-10 bg-gradient-to-br from-[#1FA3B5] via-[#178A9A] to-[#0F4651] bg-clip-text text-transparent">
                Ohne
              </Reveal>{" "}
              <Reveal delay={0.4} className="relative z-10 bg-gradient-to-br from-[#1FA3B5] via-[#178A9A] to-[#0F4651] bg-clip-text text-transparent">
                Nachzahlung.
              </Reveal>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -bottom-1 left-0 right-0 h-3 bg-[#1FA3B5]/15 rounded origin-left -z-0"
              />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 sm:mt-7 text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-xl"
          >
            Smart Repair, Komplett-Aufbereitung und persönliche Begleitung – damit der
            Leasinggeber bei der Rückgabe nichts mehr findet, wofür er Sie zur Kasse bittet.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href="#spar-check"
              data-testid="hero-cta-primary"
              className="group relative inline-flex items-center justify-center gap-2 bg-[#1FA3B5] text-white rounded-full px-6 sm:px-8 py-3.5 sm:py-4 font-semibold overflow-hidden hover:-translate-y-0.5 transition-transform shadow-xl shadow-[#1FA3B5]/40 text-sm sm:text-base"
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              <span className="absolute inset-0 rounded-full ring-2 ring-[#1FA3B5]/40 animate-pulse" />
              <Timer className="w-5 h-5 relative z-10" />
              <span className="relative z-10">60-Sek. Spar-Check starten</span>
              <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={buildWhatsAppLink("Hallo AutoCoach, ich möchte ein kostenloses Erstgespräch zur Leasingrückgabe.")}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 rounded-full px-6 sm:px-8 py-3.5 sm:py-4 font-semibold hover:bg-slate-50 hover:border-slate-300 hover:-translate-y-0.5 transition-all text-sm sm:text-base"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Beratung
            </a>
          </motion.div>

          {/* Trust row – kompakter & mittig auf mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 sm:mt-14 mx-auto sm:mx-0 inline-flex sm:grid sm:grid-cols-3 gap-5 sm:gap-6 sm:max-w-lg bg-white/60 sm:bg-transparent backdrop-blur-sm sm:backdrop-blur-none rounded-2xl sm:rounded-none px-4 sm:px-0 py-3 sm:py-0 border border-slate-100 sm:border-0 items-center"
          >
            <div data-testid="hero-stat-savings" className="text-center sm:text-left">
              <p className="font-heading text-lg sm:text-3xl lg:text-4xl font-bold text-slate-900 whitespace-nowrap">
                <Counter to={850} prefix="Ø " suffix=" €" />
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 leading-tight">gesparte<br className="hidden sm:block" /> <span className="sm:hidden">/ </span>Nachzahlung</p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-slate-200 self-center" />
            <div className="w-px h-8 bg-slate-200 sm:hidden" />
            <div data-testid="hero-stat-cases" className="text-center sm:text-left">
              <p className="font-heading text-lg sm:text-3xl lg:text-4xl font-bold text-slate-900">
                <Counter to={200} suffix="+" />
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 leading-tight">Rückgaben</p>
            </div>
            <div className="w-px h-8 bg-slate-200 sm:hidden" />
            <div data-testid="hero-stat-rating" className="text-center sm:text-left">
              <div className="flex items-center gap-1 justify-center sm:justify-start">
                <p className="font-heading text-lg sm:text-3xl lg:text-4xl font-bold text-slate-900">5,0</p>
                <Star className="w-3.5 h-3.5 sm:w-5 sm:h-5 fill-[#FFB400] text-[#FFB400]" />
              </div>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 leading-tight">Bewertung</p>
            </div>
          </motion.div>
        </motion.div>

        {/* ===== RIGHT: Image Collage ===== */}
        <div className="lg:col-span-6 relative h-[400px] sm:h-[560px] lg:h-[720px] order-2">
          {/* MAIN IMAGE — BMW M5 dark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-0 right-0 w-[88%] sm:w-[82%] lg:w-[78%] h-[68%] sm:h-[64%] lg:h-[62%] rounded-2xl sm:rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-900/25 border border-white/60"
          >
            {/* Light scan beam — premium effect */}
            <motion.div
              initial={{ x: "-120%" }}
              animate={{ x: "220%" }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.5 }}
              className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-[-20deg] z-10 pointer-events-none"
            />

            <motion.img
              src={HERO_IMAGES.main}
              alt="BMW Leasingfahrzeug rückgabe-fertig"
              className="w-full h-full object-cover"
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />

            {/* Top badge */}
            <div className="absolute top-4 sm:top-5 left-4 sm:left-5">
              <div className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] sm:text-[11px] font-bold text-slate-900 uppercase tracking-wider shadow-lg">
                <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#1FA3B5]" />
                Rückgabe-Ready
              </div>
            </div>

            {/* Made in Germany rotating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="absolute top-4 sm:top-5 right-4 sm:right-5 z-20"
            >
              <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                <motion.svg
                  viewBox="0 0 100 100"
                  className="w-full h-full absolute inset-0"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <defs>
                    <path id="circ" d="M 50, 50 m -36, 0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                  </defs>
                  <text className="fill-white text-[10px] sm:text-[11px] font-bold tracking-[0.2em] uppercase">
                    <textPath href="#circ">● MADE FOR GERMAN CARS ● BMW MERCEDES AUDI VW</textPath>
                  </text>
                </motion.svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#1FA3B5] flex items-center justify-center shadow-lg shadow-[#1FA3B5]/50">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Vorher / Nachher teaser */}
            <div className="absolute bottom-4 sm:bottom-5 left-4 sm:left-5 right-4 sm:right-5 flex items-center gap-1.5 sm:gap-2">
              <div className="flex-1 bg-white/95 backdrop-blur rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-red-600">Vorher</p>
                <p className="text-[10px] sm:text-xs font-semibold text-slate-900 leading-tight">Kratzer, Dellen, Spuren</p>
              </div>
              <div className="text-white text-lg sm:text-xl drop-shadow">→</div>
              <div className="flex-1 bg-[#1FA3B5] rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-lg shadow-[#1FA3B5]/40">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-white/90">Nachher</p>
                <p className="text-[10px] sm:text-xs font-semibold text-white leading-tight">Übergabe-Zustand</p>
              </div>
            </div>
          </motion.div>

          {/* SECONDARY IMAGE — Mercedes (bottom-left) */}
          <motion.div
            initial={{ opacity: 0, x: -30, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-0 left-0 w-[55%] sm:w-[52%] lg:w-[50%] h-[36%] sm:h-[40%] lg:h-[38%] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/30 border-2 sm:border-4 border-white"
          >
            <motion.img
              src={HERO_IMAGES.side}
              alt="Mercedes-Benz Premium Aufbereitung"
              className="w-full h-full object-cover"
              animate={{ scale: [1.05, 1, 1.05] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F4651]/70 via-transparent to-transparent" />
            <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
              <div className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/95 text-[9px] sm:text-[10px] font-bold text-slate-900 uppercase tracking-wider">
                <KeyRound className="w-3 h-3 text-[#1FA3B5]" />
                Premium Aufbereitung
              </div>
            </div>
          </motion.div>

          {/* FLOATING — Savings (hidden on smallest screens, visible from sm+) */}
          <motion.div
            initial={{ opacity: 0, x: 40, y: -20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="hidden sm:block absolute top-[6%] -left-2 lg:-left-10 bg-white rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-100 p-3.5 sm:p-4 w-[200px] sm:w-[220px] z-20"
            data-testid="hero-floating-savings"
          >
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1FA3B5] to-[#0F4651] flex items-center justify-center shadow-lg shadow-[#1FA3B5]/30">
                  <TrendingDown className="w-4 h-4 text-white" />
                </div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Letzte Ersparnis</p>
              </div>
              <p className="font-heading text-3xl font-bold text-slate-900 leading-none">
                <Counter to={1325} suffix=" €" duration={2.2} />
              </p>
              <p className="text-xs text-slate-500 mt-1">Markus H. · BMW 320d</p>
            </motion.div>
          </motion.div>

          {/* FLOATING — Spar-Check Live Tool (hidden on smallest, visible from sm+) */}
          <motion.div
            initial={{ opacity: 0, x: 40, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="hidden sm:block absolute bottom-2 right-0 lg:-right-4 bg-slate-900 text-white rounded-2xl shadow-2xl shadow-slate-900/30 p-4 sm:p-5 w-[220px] sm:w-[260px] z-20"
            data-testid="hero-floating-tool"
          >
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-xl bg-[#1FA3B5] flex items-center justify-center">
                  <Timer className="w-4 h-4 text-white" />
                </div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#5DD8E5]">Live-Tool</p>
              </div>
              <p className="font-heading text-lg font-bold leading-tight">60-Sek. Spar-Check</p>
              <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                Sofortige Hochrechnung Ihrer Ersparnis – ohne Anmeldung.
              </p>
              <a
                href="#spar-check"
                data-testid="hero-floating-tool-cta"
                className="mt-3 inline-flex items-center gap-1 text-[#5DD8E5] font-semibold text-xs hover:gap-2 transition-all"
              >
                Jetzt starten <ArrowRight className="w-3 h-3" />
              </a>
            </motion.div>
          </motion.div>

          {/* Decorative sparkle */}
          <motion.div
            initial={{ opacity: 0, rotate: -20 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="absolute top-[60%] left-[42%] z-10"
          >
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }}>
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-[#1FA3B5] drop-shadow-[0_0_15px_rgba(31,163,181,0.6)]" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ===== Brand Marquee — Dark Premium Band ===== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.3 }}
        className="relative mt-16 sm:mt-20 lg:mt-28 -mb-20 sm:-mb-24 lg:-mb-32"
      >
        <div className="relative bg-gradient-to-r from-slate-900 via-[#0F2A33] to-slate-900 py-7 sm:py-9 overflow-hidden">
          {/* Subtle radial glow */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(31,163,181,0.18)_0%,_transparent_70%)] pointer-events-none" />
          {/* Edge fade masks */}
          <div className="absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none" />

          <p className="text-center text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold text-[#5DD8E5] mb-4 sm:mb-5 px-6 relative z-10">
            ✦ Wir bereiten Fahrzeuge dieser Marken auf ✦
          </p>
          <div className="overflow-hidden relative">
            <div className="flex gap-10 sm:gap-16 lg:gap-24 animate-marquee whitespace-nowrap items-center">
              {["BMW", "Mercedes-Benz", "Audi", "Volkswagen", "Porsche", "Opel", "Volvo", "BMW", "Mercedes-Benz", "Audi", "Volkswagen", "Porsche", "Opel", "Volvo"].map((brand, i) => (
                <React.Fragment key={i}>
                  <span className="font-heading font-bold text-xl sm:text-2xl lg:text-3xl text-white tracking-tight">
                    {brand}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1FA3B5]/60 flex-shrink-0" />
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
